import { enumify, isArrayOf, isArrayOf_F, isBoolean, isDate, isDefined, isNumber, isOptional, isOptionalArrayOf, isOptionalNumber, isString } from '../lib/typeInference.js';
import { WalletPassType } from './wallet.types.js';

export const loyaltyTemplateValues = ['AD_HOC', 'PSEUDO_CURRENCY', 'STAMP_CARD', 'CLUB', 'LEVEL'] as const;
export type LoyaltyTemplate = (typeof loyaltyTemplateValues)[number];
// NB to use these in a type definition, you must use typeof, eg typeof LoyaltyTemplateEnum.PSEUDO_CURRENCY
export const LoyaltyTemplateEnum = enumify(loyaltyTemplateValues);

export const perkRedemptionMethodValues = ['AUTO', 'CODE_PDF417', 'CODE_QR', 'VOUCHER', 'PSEUDO_CURRENCY'] as const;
export type PerkRedemptionMethod = (typeof perkRedemptionMethodValues)[number];
export const PerkRedemptionMethodEnum = enumify(perkRedemptionMethodValues);

export const loyaltyDiscountTypeValues = ['PERCENTAGE', 'VALUE', 'OVERRIDE'] as const;
export type LoyaltyDiscountType = (typeof loyaltyDiscountTypeValues)[number];
export const LoyaltyDiscountTypeEnum = enumify(loyaltyDiscountTypeValues);

export const loyaltyDiscountRuleValues = [
    'CHEAPEST',
    'COSTLIEST',
    // The award will be applied in full to all matching items
    // So an award of VALUE £1.5 and with 8 matching items gives a total discount of 8 x 1. = 12
    'ALL',
    // The award will be allocated prorata across all matching items
    // So an award of VALUE £1.5 and with 8 matching items gives a total discount of £1.5 (8 x 0.1875)
    'ALL_ALLOCATE',
    // The award will be fully allocated to matching items starting with the cheapest until the award is spent
    // (PSEUDOCURRENCY only currently)
    'CHEAPEST_FIRST',
    // The award will be fully allocated to matching items starting with the costliest until the award is spent
    // (PSEUDOCURRENCY only currently)
    'COSTLIEST_FIRST'
] as const;

export type LoyaltyDiscountRule = (typeof loyaltyDiscountRuleValues)[number];
export const LoyaltyDiscountRuleEnum = enumify(loyaltyDiscountRuleValues);

export const loyaltyScopeValues = [
    // the following only find parent items for discounting
    'ITEM',
    'CATEGORY',
    'BASKET',
    'ITEM_OR_CATEGORY',
    // just child items (ie condiments)
    'CHILD_ITEM',
    // any type of item
    'ITEM_AND_CHILD_ITEM'
] as const;

export type LoyaltyScope = (typeof loyaltyScopeValues)[number];
export const LoyaltyScopeEnum = enumify(loyaltyScopeValues);

export const loyaltyDiscountChildItemRuleValues = [
    'INCLUDE', // don't use this in conjunction with scope CHILD_ITEM or ITEM_AND_CHILD_ITEM since it will double count cost
    'INCLUDE_MATCHED', // don't use this in conjunction with scope CHILD_ITEM or ITEM_AND_CHILD_ITEM since it will double count cost
    'EXCLUDE'
] as const;
export type LoyaltyDiscountChildItemRule = (typeof loyaltyDiscountChildItemRuleValues)[number];
export const LoyaltyDiscountChildItemRuleEnum = enumify(loyaltyDiscountChildItemRuleValues);
export type LoyaltyDiscountCombinationRule = (typeof loyaltyDiscountCombinationRuleValues)[number];
export const loyaltyDiscountCombinationRuleValues = ['MULTI', 'OWN_QUALIFIER', 'SINGLE'] as const;

export interface IPerkPoints {
    initial: number;
    min: number;
    max: number;
    step: number;
    redemption: number;
    maxTotalRedemptions?: number;
    totalRedeemed?: number;
    totalAwarded?: number;
}

export function isPerkPoints(points: any): points is IPerkPoints {
    return (
        isNumber(points.initial) &&
        isNumber(points.min) &&
        isNumber(points.max) &&
        isNumber(points.step) &&
        isNumber(points.redemption) &&
        isOptionalNumber(points.maxTotalRedemptions) &&
        isOptionalNumber(points.totalRedeemed) &&
        isOptionalNumber(points.totalAwarded)
    );
}

export function isPartialPerkPoints(points: any): points is Partial<IPerkPoints> {
    return (
        isOptionalNumber(points.initial) &&
        isOptionalNumber(points.min) &&
        isOptionalNumber(points.max) &&
        isOptionalNumber(points.step) &&
        isOptionalNumber(points.redemption) &&
        isOptionalNumber(points.maxTotalRedemptions) &&
        isOptionalNumber(points.totalRedeemed) &&
        isOptionalNumber(points.totalAwarded)
    );
}

export interface IPerkDiscount {
    // PERCENTAGE: value is a fraction 0...1 (1 = 100%)
    // VALUE: value is a fixed discount eg £5 off
    // OVERRIDE: value is a price override
    value: number;
    type: LoyaltyDiscountType;
    maxValue?: number;
    maxCount?: number;
    scope: LoyaltyScope;
    scopeMatchCode?: string | string[] | string[][]; // the [][] form defines groups used to select CHEAPEST/COSTLIEST items from each group
    rule: LoyaltyDiscountRule;
    childItemRule: LoyaltyDiscountChildItemRule;
    combinationRule?: LoyaltyDiscountCombinationRule;
}

export function isPerkDiscount(discount: any): discount is IPerkDiscount {
    return (
        isNumber(discount.value) &&
        loyaltyDiscountTypeValues.includes(discount.type) &&
        isOptionalNumber(discount.maxValue) &&
        isOptionalNumber(discount.maxCount) &&
        loyaltyScopeValues.includes(discount.scope) &&
        (!isDefined(discount.scopeMatchCode) ||
            isString(discount.scopeMatchCode) ||
            isArrayOf(isString, discount.scopeMatchCode) ||
            isArrayOf(isArrayOf_F(isString), discount.scopeMatchCode)) &&
        loyaltyDiscountRuleValues.includes(discount.rule) &&
        loyaltyDiscountChildItemRuleValues.includes(discount.childItemRule)
    );
}

export function isPartialPerkDiscount(discount: any): discount is Partial<IPerkDiscount> {
    return (
        isOptionalNumber(discount.value) &&
        (!isDefined(discount.type) || loyaltyDiscountTypeValues.includes(discount.type)) &&
        isOptionalNumber(discount.maxValue) &&
        isOptionalNumber(discount.maxCount) &&
        (!isDefined(discount.scope) || loyaltyScopeValues.includes(discount.scope)) &&
        (!isDefined(discount.scopeMatchCode) ||
            isString(discount.scopeMatchCode) ||
            isArrayOf(isString, discount.scopeMatchCode) ||
            isArrayOf(isArrayOf_F(isString), discount.scopeMatchCode)) &&
        (!isDefined(discount.rule) || loyaltyDiscountRuleValues.includes(discount.rule)) &&
        (!isDefined(discount.childItemRule) || loyaltyDiscountChildItemRuleValues.includes(discount.childItemRule))
    );
}

export interface IPerkDateTimeRestriction {
    startTime?: number; // seconds from midnight
    endTime?: number; // seconds from midnight
    // active days of week expressed as the sum of bit flag
    // where Monday=1, Tuesday=2, Wednesday=4, Thursday=8, Friday=16, Saturday=32, Sunday=64
    daysOfWeek?: number;
    startDate?: Date;
    endDate?: Date;
}

export function isPerkDateTimeRestriction(restriction: any): restriction is IPerkDateTimeRestriction {
    return (
        isOptionalNumber(restriction.startTime) &&
        isOptionalNumber(restriction.endTime) &&
        isOptionalNumber(restriction.daysOfWeek) &&
        isOptional(isDate, restriction.startDate) &&
        isOptional(isDate, restriction.endDate)
    );
}

export interface IPerkBasketQualification {
    matchCode?: string | string[];
    minimumItems?: number;
    minimumValue?: number;
    scope?: LoyaltyScope;
}

export function isPerkBasketQualification(qualification: any): qualification is IPerkBasketQualification {
    return (
        (isOptional(isString, qualification.matchCode) || isOptionalArrayOf(isString, qualification.matchCode)) &&
        isOptionalNumber(qualification.minimumItems) &&
        isOptionalNumber(qualification.minimumValue) &&
        (!isDefined(qualification.scope) || loyaltyScopeValues.includes(qualification.scope))
    );
}

export interface INameValuePair<KeyType = string, ValueType = string> {
    name: KeyType;
    value: ValueType;
}

function isVisualisationKeypair(input: any): input is INameValuePair<string, string> {
    return isString(input.name) && isString(input.value);
}

export const expiryLabelAnchorStyleValues = [
    'BOTTOM_LEFT_CHIP',
    'BOTTOM_RIGHT_CHIP',
    'BOTTOM_MIDDLE_CHIP',
    'BOTTOM_FULL_WIDTH_BAR',
    'TOP_LEFT_CHIP',
    'TOP_RIGHT_CHIP',
    'TOP_MIDDLE_CHIP',
    'TOP_FULL_WIDTH_BAR'
] as const;
export type ExpiryLabelAnchorStyle = (typeof expiryLabelAnchorStyleValues)[number];
export const ExpiryLabelAnchorStyleEnum = enumify(expiryLabelAnchorStyleValues);

export const expiryLabelDetailStyleValues = ['BOTTOM_MIDDLE_CHIP', 'BOTTOM_FULL_WIDTH_BAR', 'TOP_MIDDLE_CHIP', 'TOP_FULL_WIDTH_BAR'] as const;
export type ExpiryLabelDetailStyle = (typeof expiryLabelDetailStyleValues)[number];
export const ExpiryLabelDetailStyleEnum = enumify(expiryLabelDetailStyleValues);

export const awardsScreenShapeValues = ['DOUBLE_RECTANGLE', 'SINGLE_RECTANGLE', 'LARGE_SQUARE', 'SMALL_SQUARE'] as const;
export type AwardsScreenShape = (typeof awardsScreenShapeValues)[number];
export const AwardsScreenShapeEnum = enumify(awardsScreenShapeValues);

export interface IPerkVisualisation {
    templates: INameValuePair[];
    media: INameValuePair[];
    colours: INameValuePair[];
    stampImageUrl?: string;
    textColour?: string;
    stampColour?: string;
    randomiseStampPositions?: boolean;
    backgroundOverlayColour?: string;
    backgroundOverlayOpacity?: number;
    titleText?: string;
    subtitleText?: string;
    description?: string;
    availableCardCopy?: string;
    nextCardProgressCopy?: string;
    textAlignment?: string;
    subtitleTwoText?: string;
    verticalAlignment?: string;
    backgroundImageUrl?: string;
    showExpiry?: boolean;
    wallet?: IPerkWalletVisualisation;
    expiryLabelAnchorStyle?: ExpiryLabelAnchorStyle;
    expiryLabelDetailStyle?: ExpiryLabelDetailStyle;
    awardsScreenShape?: AwardsScreenShape;
}

export function isPerkVisualisation(visualisation: any): visualisation is IPerkVisualisation {
    return (
        isDefined(visualisation) &&
        isArrayOf(isVisualisationKeypair, visualisation.templates) &&
        isArrayOf(isVisualisationKeypair, visualisation.media) &&
        isArrayOf(isVisualisationKeypair, visualisation.colours) &&
        isOptional(isString, visualisation.stampImageUrl) &&
        isOptional(isString, visualisation.textColour) &&
        isOptional(isString, visualisation.stampColour) &&
        isOptional(isBoolean, visualisation.randomiseStampPositions) &&
        isOptional(isString, visualisation.backgroundOverlayColour) &&
        isOptionalNumber(visualisation.backgroundOverlayOpacity) &&
        isOptional(isString, visualisation.titleText) &&
        isOptional(isString, visualisation.subtitleText) &&
        isOptional(isString, visualisation.description) &&
        isOptional(isString, visualisation.availableCardCopy) &&
        isOptional(isString, visualisation.nextCardProgressCopy) &&
        (!isDefined(visualisation.textAlignment) ||
            visualisation.textAlignment === 'LEFT' ||
            visualisation.textAlignment === 'CENTER' ||
            visualisation.textAlignment === 'RIGHT' ||
            visualisation.textAlignment === 'JUSTIFY') &&
        isOptional(isString, visualisation.subtitleTwoText) &&
        (!isDefined(visualisation.verticalAlignment) ||
            visualisation.verticalAlignment === 'TOP' ||
            visualisation.verticalAlignment === 'CENTER' ||
            visualisation.verticalAlignment === 'BOTTOM') &&
        isOptional(isString, visualisation.backgroundImageUrl) &&
        isOptional(isBoolean, visualisation.showExpiry) &&
        (!isDefined(visualisation.expiryLabelAnchorStyle) || expiryLabelAnchorStyleValues.includes(visualisation.expiryLabelAnchorStyle)) &&
        (!isDefined(visualisation.expiryLabelDetailStyle) || expiryLabelDetailStyleValues.includes(visualisation.expiryLabelDetailStyle)) &&
        (!isDefined(visualisation.awardsScreenShape) || awardsScreenShapeValues.includes(visualisation.awardsScreenShape))
    );
}

export function isPartialPerkVisualisation(visualisation: any): visualisation is Partial<IPerkVisualisation> {
    return (
        isDefined(visualisation) &&
        isOptionalArrayOf(isVisualisationKeypair, visualisation.templates) &&
        isOptionalArrayOf(isVisualisationKeypair, visualisation.media) &&
        isOptionalArrayOf(isVisualisationKeypair, visualisation.colours) &&
        isOptional(isString, visualisation.stampImageUrl) &&
        isOptional(isString, visualisation.textColour) &&
        isOptional(isString, visualisation.stampColour) &&
        isOptional(isBoolean, visualisation.randomiseStampPositions) &&
        isOptional(isString, visualisation.backgroundOverlayColour) &&
        isOptionalNumber(visualisation.backgroundOverlayOpacity) &&
        isOptional(isString, visualisation.titleText) &&
        isOptional(isString, visualisation.subtitleText) &&
        isOptional(isString, visualisation.description) &&
        isOptional(isString, visualisation.availableCardCopy) &&
        isOptional(isString, visualisation.nextCardProgressCopy) &&
        (!isDefined(visualisation.textAlignment) ||
            visualisation.textAlignment === 'LEFT' ||
            visualisation.textAlignment === 'CENTER' ||
            visualisation.textAlignment === 'RIGHT' ||
            visualisation.textAlignment === 'JUSTIFY') &&
        isOptional(isString, visualisation.subtitleTwoText) &&
        (!isDefined(visualisation.verticalAlignment) ||
            visualisation.verticalAlignment === 'TOP' ||
            visualisation.verticalAlignment === 'CENTER' ||
            visualisation.verticalAlignment === 'BOTTOM') &&
        isOptional(isString, visualisation.backgroundImageUrl) &&
        isOptional(isBoolean, visualisation.showExpiry) &&
        (!isDefined(visualisation.expiryLabelAnchorStyle) || expiryLabelAnchorStyleValues.includes(visualisation.expiryLabelAnchorStyle)) &&
        (!isDefined(visualisation.expiryLabelDetailStyle) || expiryLabelDetailStyleValues.includes(visualisation.expiryLabelDetailStyle)) &&
        (!isDefined(visualisation.awardsScreenShape) || awardsScreenShapeValues.includes(visualisation.awardsScreenShape))
    );
}

export interface IPerkWalletVisualisation {
    // Enabled means it is available to be used in the wallet
    enabled?: boolean;
    title?: string;
    description?: string;
    terms?: string;
    link?: string;
    // Priority determines which award gets used for the wallet data, only the highest priority pass will be used
    priority?: number;
    imageUrls?: INameValuePair<WalletPassType>[];
    displayPoints?: boolean;
    // If hidden, the award will not be counted in the total awards or shown in the list of award titles
    // However, it can still set the banner image in the passes
    hidden?: boolean;
}

export interface IPointPerk<IDTYPE> {
    _id: IDTYPE;
    tenantId: IDTYPE;
    title: string;
    internalTitle?: string;
    template: LoyaltyTemplate;
    redemptionMethod: PerkRedemptionMethod; // should be optional?
    points: IPerkPoints;
    // deprecate
    // maximumPointCashValue: number;
    // changed to optional (matches award)
    auto?: boolean;
    // changed to optional (matches award)
    token?: string;
    isGlobal: boolean;
    // deprecate
    // isTier: boolean;
    // isDonatable: boolean;
    terms: string;
    priority: number;
    discount: IPerkDiscount;
    dateTimeRestriction?: IPerkDateTimeRestriction;
    basketQualification?: IPerkBasketQualification;
    visualisation: IPerkVisualisation;
    locationIds?: string[];
    excludeDiscountIds?: string[];
    enabled?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export type IPointPerkCreate<IDTYPE> = Omit<IPointPerk<IDTYPE>, '_id'>;
